import { Dispatch, SetStateAction, useEffect, useRef } from 'react'
import { Socket } from 'socket.io-client'
import { createSocket } from '@/core/api/socket'
import { ChatMessage, ChatRoom } from '@/core/models/chat-room.model'
import { useGetChatRoomsJoinedByUserQuery } from '@/core/services/chat-room'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useAppSelector } from '@/redux/rootReducer'

export function useChatSocket(
  groupChats: ChatRoom[],
  privateChats: ChatRoom[],
  activeChatRoom: ChatRoom | null,
  setActiveChatRoom: Dispatch<SetStateAction<ChatRoom | null>>
) {
  const activeChatRoomRef = useRef(activeChatRoom)
  const groupChatsRef = useRef(groupChats)
  const privateChatsRef = useRef(privateChats)
  const socketRef = useRef<Socket | null>(null)

  const authUser = useAppSelector(selectAuthUser)
  const authUserId = authUser?._id

  const { refetch: refetchGroupChats } = useGetChatRoomsJoinedByUserQuery({ isGroup: true })
  const { refetch: refetchPrivateChats } = useGetChatRoomsJoinedByUserQuery({ isGroup: false })

  useEffect(() => {
    privateChatsRef.current = privateChats
  }, [privateChats])

  useEffect(() => {
    groupChatsRef.current = groupChats
  }, [groupChats])

  useEffect(() => {
    activeChatRoomRef.current = activeChatRoom
  }, [activeChatRoom])

  useEffect(() => {
    if (!authUserId) return

    const initSocket = (userId: string) => {
      const socket = createSocket({ auth: { userId } })

      // if (authUser?._id) {
      //   socketInstance.emit('online', authUser._id)
      // }

      const handleGroupCreated = (newChatRoom: ChatRoom) => {
        if (newChatRoom.users.some((user) => user._id === userId)) {
          refetchGroupChats()
        }
      }

      const handleNewMessage = ({ chatRoomId, message }: { chatRoomId: string; message: ChatMessage }) => {
        if (chatRoomId === activeChatRoomRef.current?._id) {
          setActiveChatRoom((prev) => {
            if (!prev) return prev
            return {
              ...prev,
              messages: [...prev.messages, message],
            }
          })
          return
        }

        const isKnownPrivateChat = privateChatsRef.current?.some((chat) => chat._id === chatRoomId)
        const isKnownGroupChat = groupChatsRef.current?.some((chat) => chat._id === chatRoomId)

        if (isKnownPrivateChat) {
          refetchPrivateChats()
        }
        if (isKnownGroupChat) {
          refetchGroupChats()
        }

        if (!isKnownPrivateChat && !isKnownGroupChat) {
          refetchPrivateChats()
          refetchGroupChats()
        }
      }

      const handleMessageRead = ({ chatRoomId, messageId, userId }: { chatRoomId: string; messageId: string; userId: string }) => {
        if (chatRoomId === activeChatRoomRef.current?._id) {
          setActiveChatRoom((prev) => {
            if (!prev) return prev
            return {
              ...prev,
              messages: prev.messages.map((message) => {
                if (message._id === messageId) {
                  const readBy = message.readBy || []
                  if (!readBy.includes(userId)) {
                    return {
                      ...message,
                      readBy: [...readBy, userId],
                    }
                  }
                }
                return message
              }),
            }
          })
        }

        const isKnownPrivateChat = privateChatsRef.current?.some((chat) => chat._id === chatRoomId)
        const isKnownGroupChat = groupChatsRef.current?.some((chat) => chat._id === chatRoomId)

        if (isKnownPrivateChat) {
          refetchPrivateChats()
        }
        if (isKnownGroupChat) {
          refetchGroupChats()
        }
      }

      const handleAllMessagesRead = ({ chatRoomId, userId }: { chatRoomId: string; userId: string }) => {
        if (chatRoomId === activeChatRoomRef.current?._id) {
          setActiveChatRoom((prev) => {
            if (!prev) return prev
            return {
              ...prev,
              messages: prev.messages.map((message) => {
                if (message.author !== userId) {
                  const readBy = message.readBy || []
                  if (!readBy.includes(userId)) {
                    return {
                      ...message,
                      readBy: [...readBy, userId],
                    }
                  }
                }
                return message
              }),
            }
          })
        }

        const isKnownPrivateChat = privateChatsRef.current?.some((chat) => chat._id === chatRoomId)
        const isKnownGroupChat = groupChatsRef.current?.some((chat) => chat._id === chatRoomId)

        if (isKnownPrivateChat) {
          refetchPrivateChats()
        }
        if (isKnownGroupChat) {
          refetchGroupChats()
        }
      }

      const handleChatRoomUpdate = (chatRoom: ChatRoom) => {
        const isUserInChatRoom = chatRoom.users.some((user) => {
          const userId = typeof user === 'string' ? user : user._id
          return userId === authUser?._id
        })

        if (!isUserInChatRoom) {
          return
        }

        if (chatRoom._id === activeChatRoomRef.current?._id) {
          setActiveChatRoom(chatRoom)
        }
      }

      socket.on('group_chat_created', handleGroupCreated)
      socket.on('new_message', handleNewMessage)
      socket.on('message_read', handleMessageRead)
      socket.on('all_messages_read', handleAllMessagesRead)
      socket.on('chat_room', handleChatRoomUpdate)

      socket.on('connect', () => {
        socket.emit('online', userId)
      })

      socketRef.current = socket

      return () => {
        socket.off('group_chat_created', handleGroupCreated)
        socket.off('new_message', handleNewMessage)
        socket.off('message_read', handleMessageRead)
        socket.off('all_messages_read', handleAllMessagesRead)
        socket.off('chat_room', handleChatRoomUpdate)
        socket.off('connect')
        socket.off('disconnect')
        socket.disconnect()
      }
    }

    const unsubscribe = initSocket(authUserId)

    return () => {
      unsubscribe()
    }
  }, [authUserId, refetchGroupChats, setActiveChatRoom])

  useEffect(() => {
    if (!authUserId) return
    socketRef.current?.emit(
      'join_chat',
      privateChats.map((chat) => chat._id)
    )
  }, [authUserId, privateChats])

  useEffect(() => {
    if (!authUserId) return
    socketRef.current?.emit(
      'join_chat',
      groupChats.map((chat) => chat._id)
    )
  }, [authUserId, groupChats])

  useEffect(() => {
    if (activeChatRoom) {
      socketRef.current?.emit('join_chat', activeChatRoom._id)
    }
  }, [activeChatRoom])
}
